import type { Board } from '@common/models/board';

/**
 * FE#332 (part 2) — resolve a campaign/drip audience (`boardIDArray`) to the
 * NAMES of the target groups, so the calendar can reveal WHICH groups an event
 * hits (a customer running the same Follow-Up across many same-named groups
 * across accounts can't tell "3 groups" in Maylin from "3 groups" in Zara).
 *
 * Kept as pure helpers so the resolution is enriched ONCE in the lean-event
 * selector (which already warns against per-event board lookups / the N+1
 * pattern), not recomputed per chip render.
 */

/** audience-board id → name, for O(1) lookup when enriching many events. Blank
 *  names are kept out so a nameless board never shows as an empty tooltip line. */
export const buildBoardNameMap = (
  boards: ReadonlyArray<Pick<Board, '_id' | 'name'>>,
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const b of boards ?? []) {
    const id = b?._id != null ? String(b._id) : '';
    const name = (b?.name ?? '').trim();
    if (id && name) map[id] = name;
  }
  return map;
};

/** Resolve a `boardIDArray` to its target group names, in order. Ids we can't
 *  name (not loaded / deleted) are skipped, so `names.length` may be < the
 *  audience count — the caller surfaces the remainder as "+N more". */
export const resolveGroupNames = (
  boardIDArray: ReadonlyArray<string | number> | undefined,
  nameMap: Record<string, string>,
): string[] => {
  const out: string[] = [];
  for (const raw of boardIDArray ?? []) {
    const name = nameMap[String(raw)];
    if (name) out.push(name);
  }
  return out;
};

/**
 * Build the hover-tooltip text for a calendar event's target groups. Kept out
 * of the cell (space-tight) and revealed on hover. `count` is the full audience
 * size (`boardIDArray.length`); any groups we couldn't name are summarised as a
 * trailing "+N more" line so the total is never misrepresented. Returns '' when
 * there is nothing nameable (caller then shows no group tooltip).
 */
export const audienceTooltipText = (
  names: readonly string[],
  count: number,
  header = 'Groups',
): string => {
  if (names.length === 0) return '';
  const lines = [...names];
  const missing = Math.max(0, count - names.length);
  if (missing > 0) lines.push(`+${missing} more`);
  return `${header}:\n${lines.join('\n')}`;
};
